<template>
  <div v-loading="loading" class="page">
    <el-row :gutter="12" class="mb-16px">
      <el-col :xs="12" :md="8" v-for="item in kpis" :key="item.label">
        <div class="kpi">
          <div class="kpi__icon" :style="{ background: item.bg, color: item.color }">
            <Icon :icon="item.icon" :size="18" />
          </div>
          <div>
            <div class="kpi__value">{{ item.value }}</div>
            <div class="kpi__label">{{ item.label }}</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <el-card shadow="never" class="panel mb-16px">
      <template #header>
        <div class="panel-head">
          <span>名下商家列表</span>
        </div>
      </template>
      <div class="invite-box">
        <div class="invite-tip">拓客邀请链接（发给商家，打开后进入商家中心「确认归属」；携带本代理编号）</div>
        <el-input :model-value="inviteUrl" readonly>
          <template #append>
            <el-button type="primary" @click="copyInvite">复制</el-button>
          </template>
        </el-input>
      </div>
      <el-table :data="list" stripe>
        <el-table-column label="商家 / 店铺" min-width="180">
          <template #default="{ row }">
            <div class="cell-title">{{ row.merchantName || '未命名店铺' }}</div>
            <div class="cell-sub">ID {{ row.merchantId }}</div>
          </template>
        </el-table-column>
        <el-table-column label="所属软件" min-width="120">
          <template #default="{ row }">
            <el-tag size="small" effect="plain">{{ row.softwareName || row.softwareId || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="区域" prop="regionName" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">{{ row.regionName || '-' }}</template>
        </el-table-column>
        <el-table-column label="绑定方式" width="110">
          <template #default="{ row }">
            <dict-tag
              v-if="row.bindMode != null"
              :type="DICT_TYPE.AGENT_CHANNEL_BIND_MODE"
              :value="row.bindMode"
            />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="本月交易额" width="120">
          <template #default="{ row }">
            <span class="metric">¥{{ fen2yuan(row.monthBasis) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="本月分润" width="120">
          <template #default="{ row }">
            <span class="metric profit">¥{{ fen2yuan(row.monthProfit) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="签约时间" min-width="170">
          <template #default="{ row }">{{ formatSignTime(row.signTime) }}</template>
        </el-table-column>
      </el-table>
      <el-empty v-if="!list.length" description="暂无名下商家，可通过拓客邀请链接发展商家" :image-size="72" />
    </el-card>

    <el-card shadow="never" class="panel">
      <template #header>区域商家地图</template>
      <el-row :gutter="12">
        <el-col :xs="24" :md="10">
          <el-table
            :data="regionMap"
            stripe
            size="small"
            highlight-current-row
            @current-change="onRegionPick"
          >
            <el-table-column label="区域" prop="regionName" min-width="120" />
            <el-table-column label="商家数" prop="merchantCount" width="80" />
            <el-table-column label="本月交易额" width="110">
              <template #default="{ row }">¥{{ fen2yuan(row.monthBasis) }}</template>
            </el-table-column>
            <el-table-column label="本月分润" width="100">
              <template #default="{ row }">¥{{ fen2yuan(row.monthProfit) }}</template>
            </el-table-column>
          </el-table>
        </el-col>
        <el-col :xs="24" :md="14">
          <Echart :height="320" :options="mapChartOptions" />
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { buildMerchantBelongingInviteUrl, getPortalMerchants, getPortalRegionMap } from '@/api/agent/portal'
import { DICT_TYPE } from '@/utils/dict'
import { formatDate } from '@/utils/formatTime'
import { ElMessage } from 'element-plus'

defineOptions({ name: 'AgentPortalMerchants' })

const loading = ref(false)
const list = ref<any[]>([])
const regionMap = ref<any[]>([])
const activeRegion = ref('')

const fen2yuan = (fen?: number) =>
  fen == null
    ? '0'
    : (Number(fen) / 100).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })

const kpis = computed(() => {
  const merchantCount = new Set(list.value.map((i) => i.merchantId).filter(Boolean)).size
  const basis = list.value.reduce((s, i) => s + (i.monthBasis || 0), 0)
  const profit = list.value.reduce((s, i) => s + (i.monthProfit || 0), 0)
  return [
    {
      label: '名下商家',
      value: String(merchantCount),
      icon: 'ep:shop',
      color: '#0D9488',
      bg: 'rgba(13,148,136,.12)'
    },
    {
      label: '本月交易额',
      value: `¥${fen2yuan(basis)}`,
      icon: 'ep:trend-charts',
      color: '#2563EB',
      bg: 'rgba(37,99,235,.12)'
    },
    {
      label: '本月分润',
      value: `¥${fen2yuan(profit)}`,
      icon: 'ep:money',
      color: '#D97706',
      bg: 'rgba(217,119,6,.12)'
    }
  ]
})

/** 拓客 → 商家中心归属确认；inviteCode 必须是代理编号 agentNo，禁止回退数字 id */
const inviteUrl = computed(() => {
  const agentNo = (localStorage.getItem('agent_portal_no') || '').trim()
  return buildMerchantBelongingInviteUrl(agentNo)
})

const formatSignTime = (v: unknown) => {
  if (v == null || v === '') return '-'
  return formatDate(v as any) || String(v)
}

const copyInvite = async () => {
  const agentNo = (localStorage.getItem('agent_portal_no') || '').trim()
  if (!agentNo) {
    ElMessage.warning('缺少代理编号，请重新登录代理商后台后再复制拓客链接')
    return
  }
  await navigator.clipboard.writeText(inviteUrl.value)
  ElMessage.success(`已复制：${inviteUrl.value}`)
}

const mapChartOptions = computed<EChartsOption>(() => ({
  color: ['#0e9c8d', '#3B82F6'],
  tooltip: { trigger: 'axis' },
  legend: { data: ['商家数', '本月分润(元)'] },
  grid: { left: 40, right: 40, top: 40, bottom: 30 },
  xAxis: {
    type: 'category',
    data: regionMap.value.map((i) => i.regionName)
  },
  yAxis: [
    { type: 'value', name: '商家' },
    { type: 'value', name: '分润' }
  ],
  series: [
    {
      name: '商家数',
      type: 'bar',
      barWidth: 18,
      data: regionMap.value.map((i) => i.merchantCount || 0),
      itemStyle: {
        color: (params: any) =>
          regionMap.value[params.dataIndex]?.regionName === activeRegion.value ? '#0e9c8d' : '#79bbff'
      }
    },
    {
      name: '本月分润(元)',
      type: 'line',
      smooth: true,
      yAxisIndex: 1,
      data: regionMap.value.map((i) => Number(((i.monthProfit || 0) / 100).toFixed(2)))
    }
  ]
}))

const onRegionPick = (row: any) => {
  activeRegion.value = row?.regionName || ''
}

onMounted(async () => {
  loading.value = true
  try {
    list.value = (await getPortalMerchants()) || []
    regionMap.value = (await getPortalRegionMap()) || []
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.kpi {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 8px;
  min-height: 78px;
}
.kpi__icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kpi__value {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}
.kpi__label {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.invite-box {
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #f5fbfa;
  border: 1px solid #d7f0ec;
  border-radius: 8px;
}
.invite-tip {
  margin-bottom: 8px;
  font-size: 13px;
  color: #606266;
}
.cell-title {
  font-weight: 600;
  color: #303133;
}
.cell-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}
.metric {
  font-weight: 700;
}
.profit {
  color: #0e9c8d;
}
</style>
